import { useState } from "react";

const ORGANIZATION_ID = "abel-jorge-garcia";
const CONFIRM_PHRASE = `DELETE ${ORGANIZATION_ID}`;
const COUNT_KEYS = ["actors", "opportunities", "signals", "closures", "plays", "outcomes"];

function countsMatch(a, b) {
  if (!a || !b) return false;
  return COUNT_KEYS.every((k) => a[k] === b[k]);
}

async function runPreview(tokenValue) {
  const res = await fetch(
    `/api/admin/beta-reset/preview?organizationId=${ORGANIZATION_ID}`,
    { method: "GET", headers: { "X-Admin-Reset-Token": tokenValue }, credentials: "include" }
  );
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    throw new Error((body && body.detail) || `Error ${res.status}`);
  }
  return body;
}

/**
 * Pantalla administrativa TEMPORAL para el Beta Reset de
 * "abel-jorge-garcia" -- único tenant que esta herramienta puede tocar,
 * fijo en código, sin ningún campo que lo cambie. Nunca enlazada desde
 * ninguna navegación del producto.
 *
 * Dos secciones deliberadamente separadas:
 *   1. Preview (solo lectura) -- siempre visible.
 *   2. "Ejecutar reset aprobado" -- SOLO aparece después de un preview
 *      exitoso, y exige de nuevo el token y la frase de confirmación
 *      exacta. Antes de borrar, vuelve a consultar el preview y compara
 *      contra lo que ya se mostró en pantalla -- si algo cambió, bloquea
 *      la ejecución y obliga a repetir el preview (nunca ejecuta con
 *      números que el operador no llegó a ver).
 *
 * Reglas de seguridad, todas verificadas por
 * AdminBetaResetPreview.safety.test.js:
 *   - Ningún token se persiste en el navegador entre visitas -- viven
 *     solo en el estado de React mientras la pestaña está abierta.
 *   - Ningún token va en la URL/query string -- siempre como header
 *     X-Admin-Reset-Token.
 *   - Ningún token se registra en ningún log de consola.
 *   - No hay ningún efecto que dispare una llamada solo al abrir la
 *     pantalla -- toda petición ocurre exclusivamente como consecuencia
 *     directa de un clic explícito del operador.
 *   - La llamada a /execute vive en un solo lugar, detrás de la
 *     verificación de conteos y de la frase de confirmación exacta.
 */
export default function AdminBetaResetPreview() {
  const [previewToken, setPreviewToken] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  const [executeToken, setExecuteToken] = useState("");
  const [confirmPhrase, setConfirmPhrase] = useState("");
  const [executeLoading, setExecuteLoading] = useState(false);
  const [executeResult, setExecuteResult] = useState(null);
  const [executeError, setExecuteError] = useState(null);
  const [afterCounts, setAfterCounts] = useState(null);

  const handlePreviewClick = async () => {
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewResult(null);
    setExecuteResult(null);
    setExecuteError(null);
    setAfterCounts(null);
    try {
      const body = await runPreview(previewToken);
      setPreviewResult(body);
    } catch (e) {
      setPreviewError(e.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const canExecute =
    !!previewResult && !!executeToken && confirmPhrase === CONFIRM_PHRASE && !executeLoading;

  const handleExecuteClick = async () => {
    if (!canExecute) return;
    setExecuteLoading(true);
    setExecuteError(null);
    setExecuteResult(null);
    try {
      // Re-verificación obligatoria: si lo que hay ahora mismo en la base
      // ya no coincide con lo que se mostró en el preview aprobado, NUNCA
      // se llama a /execute -- se bloquea y se obliga a repetir el preview.
      const fresh = await runPreview(executeToken);
      if (!countsMatch(fresh.counts, previewResult.counts)) {
        setExecuteError(
          "Los conteos cambiaron desde el último preview. Vuelve a hacer preview antes de ejecutar."
        );
        setPreviewResult(null);
        return;
      }

      const res = await fetch("/api/admin/beta-reset/execute", {
        method: "POST",
        headers: { "X-Admin-Reset-Token": executeToken, "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ organizationId: ORGANIZATION_ID, confirm: confirmPhrase }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        setExecuteError((body && body.detail) || `Error ${res.status}`);
        return;
      }
      setExecuteResult(body);

      // Confirmación automática (consecuencia del mismo clic, no un
      // efecto en segundo plano): vuelve a consultar el preview para
      // demostrar en pantalla que las 6 colecciones quedaron en cero.
      const after = await runPreview(executeToken);
      setAfterCounts(after.counts);
    } catch (e) {
      setExecuteError(e.message || "No se pudo contactar el servidor.");
    } finally {
      setExecuteLoading(false);
    }
  };

  const inputStyle = {
    width: "100%", padding: 10, marginBottom: 14, boxSizing: "border-box",
    background: "#131b30", border: "1px solid #1b2846", color: "#f4f4f5",
    fontFamily: "monospace", fontSize: 13,
  };
  const labelStyle = {
    fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "#a1a1aa",
    display: "block", marginBottom: 6,
  };
  const boxStyle = { marginTop: 20, padding: 16, background: "#131b30", border: "1px solid #1b2846" };

  return (
    <div style={{ minHeight: "100vh", background: "#0d1321", color: "#f4f4f5", fontFamily: "monospace", padding: "40px 24px", boxSizing: "border-box" }}>
      <div style={{ maxWidth: 520, margin: "0 auto" }}>
        <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 2, color: "#f59e0b", marginBottom: 6 }}>
          Herramienta interna temporal
        </div>
        <h1 style={{ fontSize: 18, margin: "0 0 4px" }}>Beta Reset — {ORGANIZATION_ID}</h1>
        <p style={{ fontSize: 12, color: "#a1a1aa", marginBottom: 28, lineHeight: 1.5 }}>
          Organización fija, no editable desde aquí. Primero el preview de solo lectura;
          la ejecución real solo aparece después, y vuelve a pedir el token y la frase de
          confirmación. Ningún token se guarda en este navegador.
        </p>

        {/* ---- Preview ---- */}
        <label style={labelStyle}>ADMIN_RESET_TOKEN</label>
        <input
          type="password" value={previewToken} onChange={(e) => setPreviewToken(e.target.value)}
          autoComplete="off" spellCheck={false} style={inputStyle}
        />
        <button
          onClick={handlePreviewClick}
          disabled={!previewToken || previewLoading}
          style={{
            padding: "10px 18px", background: previewToken ? "#f59e0b" : "#3f3f46",
            color: previewToken ? "#000" : "#71717a", border: "none", fontWeight: "bold",
            fontFamily: "monospace", fontSize: 13, cursor: previewToken ? "pointer" : "not-allowed",
          }}
        >
          {previewLoading ? "Consultando..." : "Ver qué se borraría"}
        </button>

        {previewError && (
          <div style={{ marginTop: 20, padding: 12, background: "#3b0d0d", border: "1px solid #7f1d1d", color: "#fca5a5", fontSize: 13 }}>
            {previewError}
          </div>
        )}

        {previewResult && (
          <div style={boxStyle}>
            <div style={{ fontSize: 11, color: "#a1a1aa", marginBottom: 10 }}>
              organizationId: <b>{previewResult.organizationId}</b>
            </div>
            <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
              <tbody>
                {COUNT_KEYS.map((key) => (
                  <tr key={key} style={{ borderTop: "1px solid #1b2846" }}>
                    <td style={{ padding: "6px 0", color: "#a1a1aa" }}>{key}</td>
                    <td style={{ padding: "6px 0", textAlign: "right", fontWeight: "bold" }}>
                      {previewResult.counts?.[key] ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ fontSize: 11, color: "#71717a", marginTop: 12 }}>
              team_members y ghl_installations nunca aparecen aquí — este reset no puede tocarlos.
            </div>
          </div>
        )}

        {/* ---- Ejecutar reset aprobado -- solo visible tras preview exitoso ---- */}
        {previewResult && (
          <div style={{ marginTop: 40, paddingTop: 24, borderTop: "1px solid #7f1d1d" }}>
            <h2 style={{ fontSize: 15, color: "#fca5a5", margin: "0 0 6px" }}>Ejecutar reset aprobado</h2>
            <p style={{ fontSize: 12, color: "#a1a1aa", marginBottom: 18, lineHeight: 1.5 }}>
              Esto va a borrar permanentemente {COUNT_KEYS.map((k) => `${k}: ${previewResult.counts?.[k] ?? 0}`).join(" · ")}.
            </p>

            <label style={labelStyle}>ADMIN_RESET_TOKEN (otra vez)</label>
            <input
              type="password" value={executeToken} onChange={(e) => setExecuteToken(e.target.value)}
              autoComplete="off" spellCheck={false} style={inputStyle}
            />

            <label style={labelStyle}>Escribe exactamente: {CONFIRM_PHRASE}</label>
            <input
              type="text" value={confirmPhrase} onChange={(e) => setConfirmPhrase(e.target.value)}
              autoComplete="off" spellCheck={false} style={inputStyle}
            />

            <button
              onClick={handleExecuteClick}
              disabled={!canExecute}
              style={{
                padding: "10px 18px", background: canExecute ? "#dc2626" : "#3f3f46",
                color: canExecute ? "#fff" : "#71717a", border: "none", fontWeight: "bold",
                fontFamily: "monospace", fontSize: 13, cursor: canExecute ? "pointer" : "not-allowed",
              }}
            >
              {executeLoading ? "Borrando..." : "Borrar los datos beta de Abel"}
            </button>

            {executeError && (
              <div style={{ marginTop: 20, padding: 12, background: "#3b0d0d", border: "1px solid #7f1d1d", color: "#fca5a5", fontSize: 13 }}>
                {executeError}
              </div>
            )}

            {executeResult && (
              <div style={{ ...boxStyle, borderColor: "#166534" }}>
                <div style={{ color: "#4ade80", fontWeight: "bold", marginBottom: 10 }}>
                  Reset ejecutado correctamente.
                </div>
                <div style={{ fontSize: 12, color: "#a1a1aa", marginBottom: 4 }}>
                  Respaldo: <span style={{ color: "#f4f4f5" }}>{executeResult.backupFile}</span>
                </div>
                <div style={{ fontSize: 12, color: "#a1a1aa", marginBottom: 12 }}>
                  Timestamp: <span style={{ color: "#f4f4f5" }}>{executeResult.timestamp}</span>
                </div>
                <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                  <tbody>
                    {COUNT_KEYS.map((key) => (
                      <tr key={key} style={{ borderTop: "1px solid #1b2846" }}>
                        <td style={{ padding: "6px 0", color: "#a1a1aa" }}>{key} borrados</td>
                        <td style={{ padding: "6px 0", textAlign: "right", fontWeight: "bold" }}>
                          {executeResult.deletedCounts?.[key] ?? "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {afterCounts && (
                  <>
                    <div style={{ fontSize: 11, color: "#71717a", marginTop: 16, marginBottom: 6 }}>
                      Verificación automática — preview inmediatamente después del borrado:
                    </div>
                    <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                      <tbody>
                        {COUNT_KEYS.map((key) => (
                          <tr key={key} style={{ borderTop: "1px solid #1b2846" }}>
                            <td style={{ padding: "6px 0", color: "#a1a1aa" }}>{key}</td>
                            <td style={{ padding: "6px 0", textAlign: "right", fontWeight: "bold", color: afterCounts[key] === 0 ? "#4ade80" : "#fca5a5" }}>
                              {afterCounts[key]}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
